import axiosInstance from './axiosConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Types (matching the backend models)
export interface Lead {
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company: string;
  title?: string;
  industry?: string;
  numberOfEmployees?: number;
  annualRevenue?: number;
  status: 'New' | 'Contacted' | 'Qualified' | 'Unqualified' | 'Converted' | 'Lost';
  rating?: 'Hot' | 'Warm' | 'Cold';
  leadSource?: string;
  assignedTo?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Account {
  _id?: string;
  name: string;
  type: 'Prospect' | 'Customer' | 'Partner' | 'Vendor' | 'Competitor' | 'Other';
  industry?: string;
  annualRevenue?: number;
  numberOfEmployees?: number;
  website?: string;
  phone?: string;
  email?: string;
  rating?: 'Hot' | 'Warm' | 'Cold';
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  billingAddress?: Address;
  shippingAddress?: Address;
  primaryContact?: {
    name?: string;
    email?: string;
    phone?: string;
    title?: string;
  };
  assignedTo?: string;
  owner?: string;
  status: 'Active' | 'Inactive' | 'Pending';
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Contact {
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobile?: string;
  title?: string;
  department?: string;
  account: string | Account;
  isPrimary?: boolean;
  type?: 'Decision Maker' | 'Influencer' | 'End User' | 'Technical' | 'Executive' | 'Other';
  assignedTo?: string | { _id: string; name: string; email: string };
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Opportunity {
  _id?: string;
  name: string;
  account: string | Account;
  accountName?: string;
  stage: 'Prospect' | 'First Call' | 'Demo' | 'POC' | 'RFI/RFQ' | 'Negotiation' | 'Closed Won' | 'Closed Lost';
  amount: number;
  currency?: string;
  probability: number;
  expectedCloseDate: Date | string;
  actualCloseDate?: Date | string;
  description?: string;
  nextStep?: string;
  type?: 'New Business' | 'Existing Business' | 'Renewal';
  leadSource?: string;
  assignedTo?: string;
  contact?: string | Contact;
  primaryContact?: string | Contact;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Contract {
  _id?: string;
  contractNumber?: string;
  name: string;
  title?: string;
  account: string | Account;
  accountName?: string;
  opportunity?: string | Opportunity;
  partner?: string | Account;
  status: 'Draft' | 'In Approval' | 'Active' | 'Expired' | 'Terminated' | 'Renewed';
  type?: 'Service Agreement' | 'Maintenance' | 'License' | 'NDA' | 'Purchase' | 'Subscription' | 'Other';
  startDate: Date | string;
  endDate: Date | string;
  renewalDate?: Date | string;
  signedDate?: Date | string;
  signedBy?: string;
  contractValue: number;
  currency?: string;
  paymentTerms?: 'Monthly' | 'Quarterly' | 'Semi-Annual' | 'Annual' | 'One-Time' | 'Custom';
  billingFrequency?: 'Monthly' | 'Quarterly' | 'Semi-Annual' | 'Annual';
  autoRenew?: boolean;
  description?: string;
  owner?: string | { _id: string; name: string; email: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface Invoice {
  _id?: string;
  invoiceNumber: string;
  account: string | Account;
  contact?: string | Contact;
  opportunity?: string | Opportunity;
  contract?: string | Contract;
  status: 'Draft' | 'Sent' | 'Viewed' | 'Partially Paid' | 'Paid' | 'Overdue' | 'Cancelled' | 'Refunded';
  issueDate: Date | string;
  dueDate: Date | string;
  currency?: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
  lineItems?: Array<{
    description: string;
    product?: string;
    quantity: number;
    unitPrice: number;
    discount?: number;
    tax?: number;
    total: number;
  }>;
  subtotal: number;
  totalDiscount?: number;
  totalTax?: number;
  totalAmount: number;
  amountPaid?: number;
  amountDue?: number;
  paymentTerms?: 'Net 15' | 'Net 30' | 'Net 45' | 'Net 60' | 'Due on Receipt' | 'Custom';
  notes?: string;
  createdBy?: string | { _id: string; name: string; email: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface Quotation {
  _id?: string;
  quotationNumber: string;
  account: string | Account;
  accountName?: string;
  contact?: string | Contact;
  opportunity?: string | Opportunity;
  opportunityName?: string;
  partner?: string | Account;
  status: 'Draft' | 'Sent' | 'Viewed' | 'Accepted' | 'Rejected' | 'Expired' | 'Converted' | 'Cancelled';
  issueDate: Date | string;
  validUntil: Date | string;
  expiryDays?: number;
  currency?: 'USD' | 'EUR' | 'GBP' | 'TRY';
  lineItems?: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    discount?: number;
    discountType?: 'percentage' | 'fixed';
    tax?: number;
    taxRate?: number;
    total: number;
  }>;
  subtotal: number;
  totalDiscount?: number;
  totalTax?: number;
  totalAmount: number;
  globalDiscount?: number;
  globalDiscountType?: 'percentage' | 'fixed';
  paymentTerms?: string;
  notes?: string;
  termsAndConditions?: string;
  approvalStatus?: 'Pending' | 'Approved' | 'Rejected' | 'Not Required';
  convertedToInvoice?: string | { _id: string; invoiceNumber: string; status: string };
  revisionNumber?: number;
  createdBy?: string | { _id: string; name: string; email: string };
  assignedTo?: string | { _id: string; name: string; email: string };
  createdAt?: string;
  updatedAt?: string;
}

interface Address {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

// CRM Service
class CRMService {
  // Dashboard
  async getDashboard() {
    const response = await axiosInstance.get('/crm/dashboard');
    return response.data;
  }

  // Leads
  async getLeads() {
    const response = await axiosInstance.get('/crm/leads');
    return response.data;
  }

  async createLead(lead: Omit<Lead, '_id'>) {
    const response = await axiosInstance.post('/crm/leads', lead);
    return response.data;
  }

  async updateLead(id: string, lead: Partial<Lead>) {
    const response = await axiosInstance.put(`/crm/leads/${id}`, lead);
    return response.data;
  }

  async deleteLead(id: string) {
    const response = await axiosInstance.delete(`/crm/leads/${id}`);
    return response.data;
  }

  async convertLead(id: string) {
    const response = await axiosInstance.post(`/crm/leads/${id}/convert`, {});
    return response.data;
  }

  // Accounts
  async getAccounts() {
    const response = await axiosInstance.get('/crm/accounts');
    return response.data;
  }

  async createAccount(account: Omit<Account, '_id'>) {
    const response = await axiosInstance.post('/crm/accounts', account);
    return response.data;
  }

  async updateAccount(id: string, account: Partial<Account>) {
    const response = await axiosInstance.put(`/crm/accounts/${id}`, account);
    return response.data;
  }

  async deleteAccount(id: string) {
    const response = await axiosInstance.delete(`/crm/accounts/${id}`);
    return response.data;
  }

  // Contacts
  async getContacts() {
    const response = await axiosInstance.get('/crm/contacts');
    return response.data;
  }

  async createContact(contact: Omit<Contact, '_id'>) {
    const response = await axiosInstance.post('/crm/contacts', contact);
    return response.data;
  }

  async updateContact(id: string, contact: Partial<Contact>) {
    const response = await axiosInstance.put(`/crm/contacts/${id}`, contact);
    return response.data;
  }

  async deleteContact(id: string) {
    const response = await axiosInstance.delete(`/crm/contacts/${id}`);
    return response.data;
  }

  // Opportunities
  async getOpportunities() {
    const response = await axiosInstance.get('/crm/opportunities');
    return response.data;
  }

  async createOpportunity(opportunity: Omit<Opportunity, '_id'>) {
    const response = await axiosInstance.post('/crm/opportunities', opportunity);
    return response.data;
  }

  async updateOpportunity(id: string, opportunity: Partial<Opportunity>) {
    const response = await axiosInstance.put(`/crm/opportunities/${id}`, opportunity);
    return response.data;
  }

  async deleteOpportunity(id: string) {
    const response = await axiosInstance.delete(`/crm/opportunities/${id}`);
    return response.data;
  }

  // Contracts
  async getContracts() {
    const response = await axiosInstance.get('/crm/contracts');
    return response.data;
  }

  async createContract(contract: Omit<Contract, '_id'>) {
    const response = await axiosInstance.post('/crm/contracts', contract);
    return response.data;
  }

  async updateContract(id: string, contract: Partial<Contract>) {
    const response = await axiosInstance.put(`/crm/contracts/${id}`, contract);
    return response.data;
  }

  async deleteContract(id: string) {
    const response = await axiosInstance.delete(`/crm/contracts/${id}`);
    return response.data;
  }

  // Quotations
  async getQuotations(params?: { opportunityId?: string }) {
    const response = await axiosInstance.get('/crm/quotations', { params });
    return response.data;
  }

  async getQuotation(id: string) {
    const response = await axiosInstance.get(`/crm/quotations/${id}`);
    return response.data;
  }

  // Invoices
  async getInvoices() {
    const response = await axiosInstance.get('/crm/invoices');
    return response.data;
  }

  async getInvoice(id: string) {
    const response = await axiosInstance.get(`/crm/invoices/${id}`);
    return response.data;
  }
}

const crmServiceInstance = new CRMService();
export default crmServiceInstance;